import { useState, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/context/AuthContext';
import type { SportPreference } from '@/context/AuthContext';

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentName: string;
  currentAvatar: string;
  onSave: (data: { name: string; avatar: string }) => void;
}

type ActiveSection = 'main' | 'avatar' | 'sport' | 'password';

export default function EditProfileModal({
  isOpen,
  onClose,
  currentName,
  currentAvatar,
  onSave,
}: EditProfileModalProps) {
  const { supabaseUser, refreshProfile, avatarUrl, sportPreference, setSportPreference } = useAuth();
  const [activeSection, setActiveSection] = useState<ActiveSection>('main');
  const [name, setName] = useState(currentName);
  const [selectedSport, setSelectedSport] = useState<SportPreference>(sportPreference);
  const [sportSaving, setSportSaving] = useState(false);
  const [sportSuccess, setSportSuccess] = useState(false);
  const [nameError, setNameError] = useState('');

  // Avatar state
  const [avatarPreview, setAvatarPreview] = useState<string | null>(currentAvatar || avatarUrl || null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarSuccess, setAvatarSuccess] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pwErrors, setPwErrors] = useState<{ current?: string; new?: string; confirm?: string }>({});
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwErrMsg, setPwErrMsg] = useState('');

  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      setAvatarError('Solo se permiten imágenes (JPG, PNG, WebP).');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setAvatarError('El archivo no puede superar los 5 MB.');
      return;
    }
    setAvatarError('');
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setAvatarPreview(e.target?.result as string);
    reader.readAsDataURL(file);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  const handleUploadAvatar = async () => {
    if (!selectedFile || !supabaseUser) return;
    setUploadingAvatar(true);
    setAvatarError('');
    try {
      const ext = selectedFile.name.split('.').pop() ?? 'jpg';
      const filePath = `${supabaseUser.id}/avatar.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, selectedFile, { upsert: true, contentType: selectedFile.type });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(filePath);
      const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;

      const { error: dbError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', supabaseUser.id);

      if (dbError) throw dbError;

      await refreshProfile();
      setAvatarPreview(publicUrl);
      setSelectedFile(null);
      setAvatarSuccess(true);
      onSave({ name, avatar: publicUrl });
      setTimeout(() => setAvatarSuccess(false), 2500);
    } catch (err: unknown) {
      setAvatarError('Error al subir la imagen. Inténtalo de nuevo.');
      console.error(err);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleRemoveAvatar = async () => {
    if (!supabaseUser) return;
    setUploadingAvatar(true);
    try {
      await supabase.from('profiles').update({ avatar_url: null }).eq('id', supabaseUser.id);
      setAvatarPreview(null);
      setSelectedFile(null);
      await refreshProfile();
    } catch { /* ignore */ }
    finally { setUploadingAvatar(false); }
  };

  if (!isOpen) return null;

  const handleSaveProfile = async () => {
    setNameError('');
    setSaveError('');
    const trimmed = name.trim();
    if (!trimmed) { setNameError('El nombre no puede estar vacío.'); return; }
    if (trimmed.length < 2) { setNameError('Mínimo 2 caracteres.'); return; }
    if (trimmed.length > 32) { setNameError('Máximo 32 caracteres.'); return; }
    if (!/^[a-zA-Z0-9_\-\.áéíóúÁÉÍÓÚñÑüÜ ]+$/.test(trimmed)) {
      setNameError('Solo letras, números, guiones y puntos.');
      return;
    }
    if (!supabaseUser) return;
    setSaving(true);
    const { error } = await supabase.from('profiles').update({ username: trimmed }).eq('id', supabaseUser.id);
    setSaving(false);
    if (error) { setSaveError('Error al guardar. Inténtalo de nuevo.'); return; }
    await refreshProfile();
    setSaveSuccess(true);
    onSave({ name: trimmed, avatar: avatarPreview ?? '' });
    setTimeout(() => { setSaveSuccess(false); onClose(); }, 1400);
  };

  const handleSavePassword = async () => {
    const errors: typeof pwErrors = {};
    setPwErrMsg('');
    if (!currentPassword) errors.current = 'Introduce tu contraseña actual.';
    if (!newPassword) errors.new = 'Introduce la nueva contraseña.';
    else if (newPassword.length < 8) errors.new = 'Mínimo 8 caracteres.';
    if (!confirmPassword) errors.confirm = 'Confirma la nueva contraseña.';
    else if (newPassword !== confirmPassword) errors.confirm = 'Las contraseñas no coinciden.';
    setPwErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setSaving(false);
    if (error) { setPwErrMsg('Error al cambiar la contraseña. Verifica la contraseña actual.'); return; }
    setPwSuccess(true);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setTimeout(() => setPwSuccess(false), 2500);
  };

  const handleSaveSport = async () => {
    setSportSaving(true);
    await setSportPreference(selectedSport);
    setSportSaving(false);
    setSportSuccess(true);
    setTimeout(() => setSportSuccess(false), 2000);
  };

  const SPORT_OPTIONS: { key: SportPreference; label: string; icon: string; desc: string; color: string }[] = [
    { key: 'football',   label: 'Fútbol',       icon: 'ri-football-line',   desc: 'Estadísticas, ligas y cuotas de fútbol',   color: '#00e87a' },
    { key: 'tennis',     label: 'Tenis',         icon: 'ri-ping-pong-line',  desc: 'Rankings ATP/WTA, torneos y apuestas',    color: '#ffd700' },
    { key: 'basketball', label: 'Baloncesto',    icon: 'ri-basketball-line', desc: 'NBA, ACB y estadísticas de basket',       color: '#ff6b35' },
  ];

  const TABS: { key: ActiveSection; label: string; icon: string }[] = [
    { key: 'main',     label: 'Usuario',    icon: 'ri-user-line' },
    { key: 'avatar',   label: 'Foto',       icon: 'ri-image-line' },
    { key: 'sport',    label: 'Deporte',    icon: 'ri-trophy-line' },
    { key: 'password', label: 'Contraseña', icon: 'ri-lock-line' },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#2a2d3a]">
          <h2 className="text-white font-bold text-lg">Editar Perfil</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-[#6b7280] hover:text-white hover:bg-white/10 rounded-lg transition-all cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[#2a2d3a]">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveSection(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex-1 justify-center ${
                activeSection === tab.key
                  ? 'text-[#00e87a] border-b-2 border-[#00e87a] bg-[#00e87a]/5'
                  : 'text-[#6b7280] hover:text-white'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${tab.icon} text-sm`}></i>
              </div>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── SECTION: Nombre ── */}
        {activeSection === 'main' && (
          <div className="px-6 py-6 space-y-5">
            <div className="bg-[#0d0f14] border border-[#2a2d3a] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-4 h-4 flex items-center justify-center text-[#00e87a]">
                  <i className="ri-information-line text-sm"></i>
                </div>
                <span className="text-[#00e87a] text-xs font-medium">Tu nombre aparecerá en ranking, ligas y comunidad</span>
              </div>
              <p className="text-[#6b7280] text-xs">El cambio se aplica en toda la plataforma al instante.</p>
            </div>
            <div>
              <label className="block text-[#a8b2c1] text-xs font-medium mb-2">Nombre de usuario</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#6b7280]">
                  <i className="ri-at-line text-sm"></i>
                </div>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => { setName(e.target.value); setNameError(''); setSaveError(''); }}
                  placeholder="Tu nombre de usuario"
                  maxLength={32}
                  className={`w-full bg-[#0d0f14] border ${nameError ? 'border-[#ef4444]' : 'border-[#2a2d3a]'} text-white text-sm rounded-xl pl-9 pr-4 py-3 focus:outline-none focus:border-[#00e87a]/60 transition-colors placeholder-[#3d4150]`}
                />
              </div>
              {nameError && <p className="text-[#ef4444] text-xs mt-1.5">{nameError}</p>}
              {saveError && <p className="text-[#ef4444] text-xs mt-1.5">{saveError}</p>}
              <p className="text-[#3d4150] text-xs mt-1 text-right">{name.length}/32</p>
            </div>
            {saveSuccess ? (
              <div className="flex items-center justify-center gap-2 py-3 bg-[#00e87a]/15 rounded-xl border border-[#00e87a]/30">
                <i className="ri-check-line text-[#00e87a] text-sm"></i>
                <span className="text-[#00e87a] text-sm font-bold">¡Nombre actualizado!</span>
              </div>
            ) : (
              <button
                onClick={handleSaveProfile}
                disabled={saving}
                className="w-full py-3 bg-[#00e87a] text-black font-bold text-sm rounded-xl hover:bg-[#00d070] transition-colors cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <><i className="ri-loader-4-line text-sm animate-spin"></i> Guardando...</>
                ) : (
                  <><i className="ri-save-line text-sm"></i> Guardar nombre</>
                )}
              </button>
            )}
          </div>
        )}

        {/* ── SECTION: Foto de perfil ── */}
        {activeSection === 'avatar' && (
          <div className="px-6 py-6 space-y-5">
            {/* Current avatar preview */}
            <div className="flex flex-col items-center gap-3">
              <div className="relative">
                <div className="w-24 h-24 rounded-full overflow-hidden bg-[#2a2d3a] border-2 border-[#2a2d3a] flex items-center justify-center">
                  {avatarPreview ? (
                    <img
                      src={avatarPreview}
                      alt="Avatar"
                      className="w-full h-full object-cover object-top"
                      onError={() => setAvatarPreview(null)}
                    />
                  ) : (
                    <i className="ri-user-line text-[#6b7280] text-4xl"></i>
                  )}
                </div>
                {avatarPreview && (
                  <button
                    onClick={handleRemoveAvatar}
                    disabled={uploadingAvatar}
                    className="absolute -top-1 -right-1 w-6 h-6 flex items-center justify-center bg-[#ef4444] rounded-full cursor-pointer hover:bg-[#dc2626] transition-colors"
                    title="Eliminar foto"
                  >
                    <i className="ri-close-line text-white text-xs"></i>
                  </button>
                )}
              </div>
              <p className="text-[#6b7280] text-xs text-center">
                {avatarPreview ? 'Tu foto de perfil actual' : 'Sin foto de perfil'}
              </p>
            </div>

            {/* Drop zone */}
            <div
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                isDragging
                  ? 'border-[#00e87a] bg-[#00e87a]/10'
                  : 'border-[#2a2d3a] hover:border-[#00e87a]/40 hover:bg-white/2'
              }`}
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/5 mx-auto mb-3">
                <i className="ri-upload-cloud-2-line text-[#6b7280] text-xl"></i>
              </div>
              <p className="text-white text-sm font-semibold mb-1">
                {selectedFile ? selectedFile.name : 'Arrastra o haz clic para subir'}
              </p>
              <p className="text-[#6b7280] text-xs">JPG, PNG, WebP · Máx. 5 MB</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileSelect(f); }}
              />
            </div>

            {avatarError && (
              <p className="text-[#ef4444] text-xs text-center">{avatarError}</p>
            )}

            {avatarSuccess ? (
              <div className="flex items-center justify-center gap-2 py-3 bg-[#00e87a]/15 rounded-xl border border-[#00e87a]/30">
                <i className="ri-check-line text-[#00e87a] text-sm"></i>
                <span className="text-[#00e87a] text-sm font-bold">¡Foto actualizada correctamente!</span>
              </div>
            ) : (
              <button
                onClick={handleUploadAvatar}
                disabled={!selectedFile || uploadingAvatar}
                className="w-full py-3 bg-[#00e87a] text-black font-bold text-sm rounded-xl hover:bg-[#00d070] transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {uploadingAvatar ? (
                  <><i className="ri-loader-4-line text-sm animate-spin"></i> Subiendo...</>
                ) : (
                  <><i className="ri-upload-2-line text-sm"></i> Guardar foto de perfil</>
                )}
              </button>
            )}
          </div>
        )}

        {/* ── SECTION: Deporte ── */}
        {activeSection === 'sport' && (
          <div className="px-6 py-6 space-y-4">
            <div className="bg-[#0d0f14] border border-[#2a2d3a] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-4 h-4 flex items-center justify-center text-[#ffd700]">
                  <i className="ri-information-line text-sm"></i>
                </div>
                <span className="text-[#ffd700] text-xs font-medium">Personaliza tu experiencia</span>
              </div>
              <p className="text-[#6b7280] text-xs">El deporte seleccionado personaliza tu feed y las estadísticas que ves por defecto.</p>
            </div>
            <div className="space-y-3">
              {SPORT_OPTIONS.map((opt) => {
                const isSelected = selectedSport === opt.key;
                return (
                  <button
                    key={opt.key}
                    onClick={() => setSelectedSport(opt.key)}
                    className={`w-full flex items-center gap-4 px-4 py-4 rounded-xl border-2 transition-all cursor-pointer text-left ${
                      isSelected
                        ? 'border-opacity-60 bg-opacity-10'
                        : 'border-[#2a2d3a] bg-[#0d0f14] hover:border-[#3a3d4a]'
                    }`}
                    style={isSelected ? {
                      borderColor: opt.color,
                      backgroundColor: `${opt.color}12`,
                    } : {}}
                  >
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${opt.color}20` }}
                    >
                      <i className={`${opt.icon} text-xl`} style={{ color: opt.color }}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-bold">{opt.label}</p>
                      <p className="text-[#6b7280] text-xs mt-0.5">{opt.desc}</p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'border-opacity-0' : 'border-[#3a3d4a]'}`}
                      style={isSelected ? { backgroundColor: opt.color, borderColor: opt.color } : {}}
                    >
                      {isSelected && <i className="ri-check-line text-black text-xs font-bold"></i>}
                    </div>
                  </button>
                );
              })}
              <button
                onClick={() => setSelectedSport(null)}
                className={`w-full flex items-center gap-4 px-4 py-4 rounded-xl border-2 transition-all cursor-pointer text-left ${
                  selectedSport === null
                    ? 'border-[#a8b2c1] bg-white/5'
                    : 'border-[#2a2d3a] bg-[#0d0f14] hover:border-[#3a3d4a]'
                }`}
              >
                <div className="w-11 h-11 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-apps-line text-xl text-[#6b7280]"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-bold">Todos los deportes</p>
                  <p className="text-[#6b7280] text-xs mt-0.5">Ver contenido de todos los deportes</p>
                </div>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedSport === null ? 'bg-[#a8b2c1] border-[#a8b2c1]' : 'border-[#3a3d4a]'}`}>
                  {selectedSport === null && <i className="ri-check-line text-black text-xs font-bold"></i>}
                </div>
              </button>
            </div>
            {sportSuccess ? (
              <div className="flex items-center justify-center gap-2 py-3 bg-[#00e87a]/15 rounded-xl border border-[#00e87a]/30">
                <i className="ri-check-line text-[#00e87a] text-sm"></i>
                <span className="text-[#00e87a] text-sm font-bold">¡Preferencia guardada!</span>
              </div>
            ) : (
              <button
                onClick={handleSaveSport}
                disabled={sportSaving}
                className="w-full py-3 bg-[#00e87a] text-black font-bold text-sm rounded-xl hover:bg-[#00d070] transition-colors cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {sportSaving ? (
                  <><i className="ri-loader-4-line text-sm animate-spin"></i> Guardando...</>
                ) : (
                  <><i className="ri-save-line text-sm"></i> Guardar preferencia</>
                )}
              </button>
            )}
          </div>
        )}

        {/* ── SECTION: Contraseña ── */}
        {activeSection === 'password' && (
          <div className="px-6 py-6 space-y-4">
            {[
              { label: 'Contraseña actual', val: currentPassword, set: setCurrentPassword, show: showCurrent, setShow: setShowCurrent, icon: 'ri-lock-line', err: pwErrors.current, errKey: 'current' as const, placeholder: '••••••••' },
              { label: 'Nueva contraseña', val: newPassword, set: setNewPassword, show: showNew, setShow: setShowNew, icon: 'ri-shield-keyhole-line', err: pwErrors.new, errKey: 'new' as const, placeholder: 'Mínimo 8 caracteres' },
              { label: 'Confirmar nueva contraseña', val: confirmPassword, set: setConfirmPassword, show: showConfirm, setShow: setShowConfirm, icon: 'ri-lock-2-line', err: pwErrors.confirm, errKey: 'confirm' as const, placeholder: 'Repite la nueva contraseña' },
            ].map((field) => (
              <div key={field.label}>
                <label className="block text-[#a8b2c1] text-xs font-medium mb-2">{field.label}</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#6b7280]">
                    <i className={`${field.icon} text-sm`}></i>
                  </div>
                  <input
                    type={field.show ? 'text' : 'password'}
                    value={field.val}
                    onChange={(e) => { field.set(e.target.value); setPwErrors((p) => ({ ...p, [field.errKey]: undefined })); }}
                    placeholder={field.placeholder}
                    className={`w-full bg-[#0d0f14] border ${field.err ? 'border-[#ef4444]' : field.errKey === 'confirm' && confirmPassword && confirmPassword === newPassword ? 'border-[#00e87a]/60' : 'border-[#2a2d3a]'} text-white text-sm rounded-xl pl-9 pr-10 py-3 focus:outline-none focus:border-[#00e87a]/60 transition-colors placeholder-[#3d4150]`}
                  />
                  <button type="button" onClick={() => field.setShow((p: boolean) => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-[#6b7280] hover:text-white cursor-pointer">
                    <i className={`${field.show ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`}></i>
                  </button>
                </div>
                {field.err && <p className="text-[#ef4444] text-xs mt-1.5">{field.err}</p>}
                {field.errKey === 'confirm' && !field.err && confirmPassword && confirmPassword === newPassword && (
                  <p className="text-[#00e87a] text-xs mt-1.5 flex items-center gap-1">
                    <i className="ri-check-line text-xs"></i> Las contraseñas coinciden
                  </p>
                )}
                {field.errKey === 'new' && newPassword.length > 0 && (
                  <div className="mt-2 flex gap-1">
                    {[1, 2, 3, 4].map((i) => {
                      const s = newPassword.length >= 12 && /[A-Z]/.test(newPassword) && /[0-9]/.test(newPassword) && /[^a-zA-Z0-9]/.test(newPassword) ? 4
                        : newPassword.length >= 10 && /[A-Z]/.test(newPassword) && /[0-9]/.test(newPassword) ? 3
                        : newPassword.length >= 8 ? 2 : 1;
                      return <div key={i} className={`h-1 flex-1 rounded-full ${i <= s ? s === 4 ? 'bg-[#00e87a]' : s === 3 ? 'bg-[#ffd700]' : s === 2 ? 'bg-[#ff6b35]' : 'bg-[#ef4444]' : 'bg-[#2a2d3a]'}`} />;
                    })}
                  </div>
                )}
              </div>
            ))}

            {pwErrMsg && <p className="text-[#ef4444] text-xs">{pwErrMsg}</p>}

            {pwSuccess ? (
              <div className="flex items-center justify-center gap-2 py-3 bg-[#00e87a]/15 rounded-xl border border-[#00e87a]/30">
                <i className="ri-check-line text-[#00e87a] text-sm"></i>
                <span className="text-[#00e87a] text-sm font-bold">¡Contraseña actualizada!</span>
              </div>
            ) : (
              <button
                onClick={handleSavePassword}
                disabled={saving}
                className="w-full py-3 bg-[#00e87a] text-black font-bold text-sm rounded-xl hover:bg-[#00d070] transition-colors cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2 mt-2"
              >
                {saving ? (
                  <><i className="ri-loader-4-line text-sm animate-spin"></i> Guardando...</>
                ) : 'Cambiar contraseña'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
